// Shared profile metadata — labels, prices, defaults.
// Payments are collected in a later phase; prices here are displayed only.

export const PROFILES = ["fan", "player", "scout", "trainer", "team_director"];

export const PROFILE_LABEL = {
    fan: "Fan",
    player: "Player",
    scout: "Scout",
    trainer: "Trainer",
    team_director: "Team Director / Coach",
};

export const PROFILE_TAGLINE = {
    fan: "View teams, trainers and events.",
    player: "Build a player profile. You can choose to remain private, or choose to be visible to teams and/or scouts.",
    scout: "See players who opt in to Scout visibility.",
    trainer: "Trainer profile page, message board, event tools.",
    team_director: "Team pages, message board, event tools, multiple head coaches.",
};

// Prices are shown next to paid profiles but NOT collected yet.
export const PROFILE_PRICE = {
    fan: { monthly: 0, quarterly: 0, annual: 0, free: true },
    player: { monthly: 0, quarterly: 0, annual: 0, free: true },
    scout: { monthly: 4.99, quarterly: 9.99, annual: 11.99, free: false },
    trainer: { monthly: 4.99, quarterly: 9.99, annual: 11.99, free: false },
    team_director: { monthly: 4.99, quarterly: 9.99, annual: 11.99, free: false },
};

export function priceLabel(profile) {
    const p = PROFILE_PRICE[profile];
    if (!p || p.free) return "Free";
    return `$${p.monthly.toFixed(2)}/mo · $${p.quarterly.toFixed(2)}/3mo · $${p.annual.toFixed(2)}/yr`;
}

export const PAID_PROFILES = ["scout", "trainer", "team_director"];

// ---- Account types (Standard / Pro) — Jun 2026 signup model ----
// Standard = fan + player capabilities. Pro = team/trainer capabilities (superset).
// The underlying `profiles` roles are kept for feature gating across the app.
export const ACCOUNT_TYPES = ["standard", "pro"];

export const ACCOUNT_TYPE_TO_PROFILES = {
    standard: ["player"],                          // + fan (auto)
    pro: ["team_director", "trainer", "player"],   // + fan (auto); team_director primary
};

export const ACCOUNT_TYPE_LABEL = {
    standard: "Standard",
    pro: "Pro",
};

export const ACCOUNT_TYPE_TAGLINE = {
    standard: "For fans, families and athletes.",
    pro: "For coaches, directors, trainers and scouts.",
};

export const ACCOUNT_TYPE_PRICE = {
    standard: { free: true },
    pro: { free: false, annual: 19.99 },
};

// Feature bullets. Items ending with "*" are "coming soon" (not built yet).
export const ACCOUNT_TYPE_FEATURES = {
    standard: [
        "View, add, and edit teams, trainers and events.",
        "Follow and receive updates on teams, trainers and events.",
        "Create an athlete profile for the Athlete Portal — choose to be private or visible.",
    ],
    pro: [
        "Everything in Standard, plus:",
        "Ad-free browsing — no ads anywhere on the site.",
        "Claim and moderate your team / trainer page info and discussion boards.",
        "Respond to reviews.",
        "Customize your page URL (travelsports.info/basketball/yourtext).",
        "Create recurring events.*",
        "Create custom RSVPs for your events.*",
        "View the Athlete Portal.*",
    ],
};

export const PRO_TITLES = ["Coach", "Director", "Scout"];

// Single display title chosen by the account (any account type). Dropdown order matters.
export const TITLES = ["Fan", "Athlete", "Dad", "Mom", "Coach", "Director", "Trainer", "Scout"];

// Colors that never depend on account type.
const _TITLE_ALWAYS = { Fan: "#a0a0a0", Dad: "#a0a0a0", Mom: "#a0a0a0", Athlete: "#c084fc" };
// Colors that only apply on Pro accounts; Standard accounts render these grey.
const _TITLE_PRO = { Coach: "#ff4500", Director: "#ff4500", Trainer: "#2196f3", Scout: "#ef4444" };

// Pro-aware title color. Fan/Dad/Mom always grey, Athlete always purple; Coach/Director
// orange, Trainer blue, Scout red — but only for Pro accounts (grey on Standard).
export function titleColor(title, isPro) {
    if (!title) return "#a0a0a0";
    if (_TITLE_ALWAYS[title]) return _TITLE_ALWAYS[title];
    if (_TITLE_PRO[title]) return isPro ? _TITLE_PRO[title] : "#a0a0a0";
    return "#a0a0a0";
}

// Static "full-color" map (assumes Pro) — prefer titleColor(title, isPro) when the
// viewer's/author's Pro status is known.
export const TITLE_COLOR = { ..._TITLE_ALWAYS, ..._TITLE_PRO };

// Pro accounts carry team_director or trainer capabilities.
export function isProUser(user) {
    if (user && user.is_pro) return true;
    const ps = (user && user.profiles) || [];
    return ps.includes("team_director") || ps.includes("trainer");
}

// Public-beta message shown next to paid-profile prices. Once beta closes we'll
// grant the 1-year credit to any user who signed up during beta (tracked
// server-side via `paid_profile_beta_at`).
export const BETA_NOTICE = "FREE for 90 days when you sign up during our public beta. After 90 days you'll be billed at the plan you choose. Cancel anytime.";
